// @ts-expect-error module is untyped
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { isFailed, isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import * as AssetUninstallableReason from '../constants/AssetUninstallableReason';
import { getAppShortlink, getCmsFreeSignupUrl, getCmsHubPricingUrl, getDownloadedAssetsUpdateUrl, getDownloadedAssetsUrl, getMarketingHubPricingUrl } from '../utils/urls';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const ONE_MINUTE = 60 * 1000;
export function useAssetInstallabilityMessage({
  assetUninstallableReason,
  installability,
  offeringId,
  offeringSlug,
  offeringType,
  portalId,
  requestState
}) {
  const {
    installed,
    canUpdate,
    pendingPaymentExpiry
  } = installability || {};
  if (!isFulfilled(requestState)) {
    return {
      hasAccess: false
    };
  }
  if (isFailed(requestState)) {
    return {
      hasAccess: false,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-assets-core.assets.installability.installabilityRequestFailed",
        options: {
          offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
        }
      })
    };
  }
  const hasAlreadyInstalledReason = assetUninstallableReason === AssetUninstallableReason.ALREADY_INSTALLED;
  if (installed && canUpdate && hasAlreadyInstalledReason) {
    return {
      hasAccess: true,
      message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "marketplace-ui-assets-core.assets.installability.updateAvailable_jsx",
        elements: {
          Link
        },
        options: {
          downloadsLink: {
            href: getDownloadedAssetsUpdateUrl(offeringId, offeringType)
          },
          offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
        }
      })
    };
  }
  switch (assetUninstallableReason) {
    case AssetUninstallableReason.FORBIDDEN_ADMIN_PREVIEW:
    case AssetUninstallableReason.FORBIDDEN_PUBLIC:
    case AssetUninstallableReason.ALREADY_INSTALLED:
      return {
        hasAccess: false
      };
    case AssetUninstallableReason.PROVIDER_OFFERING:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-ui-assets-core.assets.installability.providerAsset_jsx",
          elements: {
            Link
          },
          options: {
            anotherAccountLink: {
              href: getAppShortlink({
                pathname: `/website/${offeringSlug}`,
                theme: offeringSlug
              }, false)
            },
            signupLink: {
              href: getCmsFreeSignupUrl(offeringSlug)
            }
          }
        })
      };
    case AssetUninstallableReason.EXCEEDS_ASSET_LIMIT:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.assets.installability.exceedsAssetLimit",
          options: {
            offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
          }
        })
      };
    case AssetUninstallableReason.PURCHASED_NOT_INSTALLED:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-ui-assets-core.assets.installability.purchasedNotInstalled_jsx",
          elements: {
            Link
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUrl(false, offeringType)
            },
            offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
          }
        })
      };
    case AssetUninstallableReason.PENDING_PAYMENT:
      {
        const duration = /*#__PURE__*/_jsx(FormattedDuration, {
          from: new Date().valueOf(),
          to: pendingPaymentExpiry + ONE_MINUTE
        });
        return {
          hasAccess: false,
          message: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "marketplace-ui-assets-core.assets.installability.paymentPendingExpiry",
            options: {
              duration,
              offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
            }
          })
        };
      }
    case AssetUninstallableReason.PENDING_INSTALL:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-ui-assets-core.assets.installability.pendingInstall_jsx",
          elements: {
            Link
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUrl(false, offeringType)
            },
            offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
          }
        })
      };
    case AssetUninstallableReason.PENDING_UNINSTALL:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-ui-assets-core.assets.installability.pendingUninstall_jsx",
          elements: {
            Link
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUrl(false, offeringType)
            },
            offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`)
          }
        })
      };
    case AssetUninstallableReason.MISSING_REQUIRED_SUBSCRIPTION:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-ui-assets-core.assets.installability.missingRequiredSubscription_jsx",
          elements: {
            Link
          },
          options: {
            cmsHubLink: {
              external: true,
              href: getCmsHubPricingUrl()
            },
            marketingHubLink: {
              external: true,
              href: getMarketingHubPricingUrl()
            }
          }
        })
      };
    case AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.assets.installability.marketplaceAssetAccess"
        })
      };
    default:
      break;
  }
  return {
    hasAccess: true,
    message: portalId ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-assets-core.assets.installability.portalLicense",
      options: {
        offeringType: unescapedText(`marketplace-ui-assets-core.offeringType.${offeringType}_lowercase`),
        portalId
      }
    }) : undefined
  };
}